import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChartLineUp, Sparkle, TrendUp, TrendDown, Minus, Heart, Pulse,
  Warning, XCircle, CheckCircle,
} from "@phosphor-icons/react";

const HEALTH_META = {
  Healthy:   { tone: "text-emerald-300", bg: "bg-emerald-400/5",  border: "border-emerald-400/25", Icon: Heart },
  Stable:    { tone: "text-blue-300",    bg: "bg-blue-400/5",     border: "border-blue-400/25",    Icon: Pulse },
  Attention: { tone: "text-amber-300",   bg: "bg-amber-400/5",    border: "border-amber-400/25",   Icon: Warning },
  Critical:  { tone: "text-red-300",     bg: "bg-red-400/5",      border: "border-red-400/30",     Icon: XCircle },
};

function fmtValue(k) {
  const v = k.current_value;
  if (k.unit === "$") {
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${Math.round(v / 1e3)}k`;
    return `$${Math.round(v || 0).toLocaleString()}`;
  }
  if (k.unit === "%") return `${v}%`;
  return `${(v || 0).toLocaleString()}`;
}

function TrendPill({ pct, trend }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[11px] text-ink-secondary/70 inline-flex items-center gap-1"><Minus className="h-3 w-3" /> —</span>;
  }
  const Up = trend === "up" ? TrendUp : trend === "down" ? TrendDown : Minus;
  const tone = trend === "up" ? "text-emerald-300" : trend === "down" ? "text-red-300" : "text-ink-secondary";
  return <span className={`text-[11px] inline-flex items-center gap-1 ${tone}`}><Up weight="bold" className="h-3 w-3" /> {pct}%</span>;
}

export default function Analytics() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/executive");
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/analytics/insights/generate");
      if (data?.insufficient_data) toast.info("Not enough data yet");
      else toast.success(`Generated ${data.created} insights`);
      load();
    } catch { toast.error("Could not generate"); }
    finally { setGenerating(false); }
  };

  const kpis = data?.kpis || [];
  const health = data?.business_health || {};
  const insights = data?.ai_insights_v2 || [];

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <ChartLineUp weight="duotone" className="h-3 w-3" /> Executive
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Analytics</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            KPIs computed live from your real workspace data, plus derived health status per area
            and AI-generated observations. 30-day rolling window vs. the prior 30 days.
          </p>
        </div>
        <button
          data-testid="analytics-generate"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60"
        >
          <Sparkle weight="fill" className="h-4 w-4" />
          {generating ? "Analyzing…" : "Generate AI insights"}
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary">Loading…</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-kpis">
            {kpis.map((k) => {
              const meta = HEALTH_META[k.status] || HEALTH_META.Stable;
              return (
                <div
                  key={k.id}
                  data-testid={`analytics-kpi-${k.id}`}
                  className={`rounded-md border ${meta.border} ${meta.bg} p-4`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{k.name}</div>
                  <div className="mt-1.5 font-display text-2xl tracking-tight tabular-nums">{fmtValue(k)}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <TrendPill pct={k.percentage_change} trend={k.trend} />
                    <span className={`text-[10px] uppercase tracking-widest ${meta.tone}`}>{k.status}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Business Health */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="analytics-health">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
              <Heart weight="duotone" className="h-3 w-3" /> Business health
            </div>
            <h2 className="mt-0.5 font-display text-lg tracking-tight">Status by area</h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(health).map(([area, status]) => {
                const meta = HEALTH_META[status] || HEALTH_META.Stable;
                const Icon = meta.Icon;
                return (
                  <div key={area}
                    data-testid={`analytics-health-${area.toLowerCase()}`}
                    data-status={status}
                    className={`rounded-md border ${meta.border} ${meta.bg} p-3 flex items-center gap-3`}
                  >
                    <Icon weight="fill" className={`h-4 w-4 ${meta.tone}`} />
                    <div className="min-w-0">
                      <div className="text-xs text-ink-primary">{area}</div>
                      <div className={`text-[10px] uppercase tracking-widest ${meta.tone}`}>{status}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* AI Insights v2 */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="analytics-insights">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                  <Sparkle weight="duotone" className="h-3 w-3" /> AI Summary
                </div>
                <h2 className="mt-0.5 font-display text-lg tracking-tight">Executive observations</h2>
              </div>
            </div>
            {insights.length === 0 ? (
              <div className="mt-5 text-sm text-ink-secondary" data-testid="analytics-insights-empty">
                No AI insights yet. Click <span className="text-ink-primary">Generate AI insights</span> to have Claude review this workspace and record structured observations.
              </div>
            ) : (
              <ul className="mt-5 space-y-2" data-testid="analytics-insights-list">
                {insights.slice(0, 10).map((ins) => (
                  <li key={ins.id}
                    data-testid={`analytics-insight-${ins.id}`}
                    className="rounded-md border border-white/10 bg-graphite-950/40 px-4 py-3"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle weight="duotone" className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink-primary">{ins.title}</div>
                        <div className="mt-1 text-[11px] text-ink-secondary">{ins.finding}</div>
                        <div className="mt-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink-secondary/70">
                          <span className="border border-white/10 px-1.5 py-0.5 rounded-sm">{ins.category}</span>
                          <span className="border border-white/10 px-1.5 py-0.5 rounded-sm">Priority {ins.priority}</span>
                          <span className="border border-white/10 px-1.5 py-0.5 rounded-sm">{ins.status}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {data?.legacy_ai_insights?.items?.length > 0 && (
              <div className="mt-5 text-[11px] text-ink-secondary" data-testid="analytics-legacy-insights-hint">
                Legacy insights ({data.legacy_ai_insights.items.length}) from the Dashboard widget are preserved on the org record and remain visible on the AI Center Overview.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
